import { Typography } from '@breetometer/components';
import type { TourStep } from 'BreetHelpers';
import { REQUIRED_ONBOARDING_TASKS, useModalApi, useOnboardingTodoListApi } from 'BreetHooks';
import { setTourStep, useAppDispatch } from 'BreetRedux';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Loader } from '../Loader';
import {
	btnTitleMap,
	type IDialogOnboardingTodoListItemStatus,
	type IDialogOnboardingTodoListSteps,
	kybStatusMap,
} from './DialogOnboardingTodoList.helpers';
import DialogOnboardingTodoListItem from './DialogOnboardingTodoList.Item';

const DialogOnboardingTodoList = () => {
	const { closeModalDirect } = useModalApi();

	const {
		kybStatus,
		kybRejectionReason,
		isFirstProductionApiKey,
		isLoading,
		isMFAAuthentication,
		isFirstTransaction,
		isProductionWebhookUrl,
		requiredCompletedCount,
		isKybCompleted,
	} = useOnboardingTodoListApi();

	const navigate = useNavigate();

	const dispatch = useAppDispatch();

	useEffect(() => {
		import('@/layout/(protected)/KybLayout').catch(console.error);
		import('@/app/(protected)/register-business/about').catch(console.error);
	}, []);

	const startTour = useCallback(
		(tourStep: TourStep) => () => {
			dispatch(setTourStep(tourStep));
		},
		[dispatch]
	);

	const navigateTo = useCallback(
		(path: string) => {
			navigate(path)
				?.then(() => closeModalDirect('onBoardingTodoList'))
				.catch(console.error);
		},
		[closeModalDirect, navigate]
	);

	const handleItemBtnClick = useCallback(
		(onboardingSteps: IDialogOnboardingTodoListSteps) => () => {
			if (onboardingSteps === 'complete-kyb') {
				navigateTo('/complete-kyb/about');
			}

			if (onboardingSteps === 'production-webhook-url' || onboardingSteps === 'first-production-api-key') {
				navigateTo('/developer/integrations');
			}
		},
		[navigateTo]
	);

	const todoListkybStatus: IDialogOnboardingTodoListItemStatus = kybStatus ? kybStatusMap[kybStatus] : 'get-started';

	const btnTitle = kybStatus ? btnTitleMap[kybStatus] : 'Get started';

	const requiredProgress = Math.round((requiredCompletedCount / REQUIRED_ONBOARDING_TASKS) * 100);

	return (
		<div className='todo-list-container bg-primary-500'>
			<div className='todo-list-content scrollbar'>
				<Typography
					as='h4'
					className='color-neutral-50'
				>
					Just a few steps before you're Breet-ready
				</Typography>
				<Typography className='todo-list-subtitle'>Complete the required steps to unlock full platform access.</Typography>

				{isLoading ? (
					<Loader bgClassName='bg-primary-500' />
				) : (
					<>
						<div className='todo-progress'>
							<div className='todo-progress-bar'>
								<div
									className='todo-progress-fill'
									style={{ width: `${requiredProgress}%` }}
								/>
							</div>
							<Typography className='todo-progress-count'>
								{requiredCompletedCount}/{REQUIRED_ONBOARDING_TASKS} COMPLETE
							</Typography>
						</div>

						<div className='todo-list theme-border-none'>
							<DialogOnboardingTodoListItem
								header='Complete KYB'
								subHeader='Verify your business details to unlock full platform access.'
								status={isKybCompleted ? 'completed' : todoListkybStatus}
								btnTitle={isKybCompleted ? 'completed' : btnTitle}
								rejectionReason={kybRejectionReason}
								onClick={handleItemBtnClick('complete-kyb')}
							/>

							<DialogOnboardingTodoListItem
								header='First Production API Key'
								subHeader='Generate your live API key to enable real transactions.'
								status={isFirstProductionApiKey ? 'get-started' : 'completed'}
								locked={!isKybCompleted}
								onClick={handleItemBtnClick('first-production-api-key')}
							/>

							<DialogOnboardingTodoListItem
								header='Production Webhook URL'
								subHeader='Configure your webhook to receive real-time transaction updates.'
								status={isProductionWebhookUrl ? 'completed' : 'get-started'}
								locked={!isKybCompleted}
								isLast
								onClick={handleItemBtnClick('production-webhook-url')}
							/>
						</div>

						<div className='todo-list-optional theme-border-none'>
							<Typography className='todo-list-optional-label'>While you're at it</Typography>

							<DialogOnboardingTodoListItem
								variant='card'
								header='Make First Transaction'
								subHeader='Make your first live transaction to get started and enjoy full benefits.'
								status={isFirstTransaction ? 'completed' : 'pending'}
								locked={!isKybCompleted}
								lockedHint='Complete your KYB above to unlock this.'
								onClick={startTour('transaction')}
							/>

							<DialogOnboardingTodoListItem
								variant='card'
								header='Multi-Factor Authentication'
								subHeader='For multi-layer security, enable a third-party authenticator app.'
								status={isMFAAuthentication ? 'completed' : 'pending'}
								onClick={startTour('mfa')}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default DialogOnboardingTodoList;
